import '../../themes/global.scss';
import '../../themes/job_summary.scss';
import '../../themes/payment.scss';

import { useEffect, useState, type CSSProperties } from 'react';
import { Alert, Column, Row, Screen, Spinner, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { BackButton } from '~/commonComponents';
import { useAppSelector } from '~/hooks';
import { useDispatch } from 'react-redux';
import { OutlineLockClosedIcon } from '@tempmee/react-icons';

import {
  DELETE_DEFAULT_PAYMENT_ACTION,
  DELETE_PAYMENT_ACTION,
  GET_ALL_PAYMENTS_ACTION,
  SELECT_PAYMENT_ACTION,
  getAllPaymnets,
} from '../../actions';
import { createLoadingSelector } from '../../api/selectors';
import { useCheckPaymentExpiration } from '../Inbox/Notification/Reminders/hooks/useCheckPaymentExpiration';
import { BankList } from './components/BankList';
import { CardList } from './components/CardList';
import { SuspendedPostingsPopup } from './SuspendedPostingsPopup';

const officeBarStyle: CSSProperties = {
  backgroundColor: colors.neutral[100],
  height: 52,
  marginBottom: 4,
};
const loadingSelector = createLoadingSelector([
  GET_ALL_PAYMENTS_ACTION,
  SELECT_PAYMENT_ACTION,
  DELETE_PAYMENT_ACTION,
  DELETE_DEFAULT_PAYMENT_ACTION,
]);

export default function Payment() {
  const dispatch = useDispatch();
  const [selectedPayment, setSelectedPayment] = useState<Record<string, unknown> | null>(null);
  const isLoading = useAppSelector((state) => loadingSelector(state));
  const selectedChildOffice = useAppSelector((state) => state.user.selectedChildOffice);
  const childOffices = useAppSelector((state) => state.user.childOffices);

  const { isExpired, isExpiring } = useCheckPaymentExpiration();

  useEffect(() => {
    dispatch(getAllPaymnets());
  }, [dispatch]);

  return (
    <>
      <Screen variant="standard">
        <Screen.Header start={<BackButton />} title="Payment Information">
          <Row align="center" justify="center" gap={1}>
            <Typography italic color={colors.neutral[500]}>
              GoTu works with Stripe &amp; Plaid to secure your payment information.
            </Typography>
            <OutlineLockClosedIcon color={colors.neutral[500]} />
          </Row>
        </Screen.Header>
        <Screen.Body inset>
          <Column gap={3} style={{ minHeight: 400 }}>
            {isExpired && (
              <Alert
                variant="hard-warning"
                description="Your default payment method has expired. Please update your payment method to confirm future professionals."
              />
            )}

            {isExpiring && (
              <Alert
                variant="soft-warning"
                description="Your default payment method is about to expire. Please update your payment method to confirm future professionals."
              />
            )}

            {childOffices.length > 1 && (
              <Row align="center" pl={8} style={officeBarStyle}>
                <Typography size="body_large" bold color={colors.neutral[600]}>
                  Office Name:{' '}
                  <Typography as="span" size="body_large" color={colors.neutral[600]}>
                    {selectedChildOffice?.office_name}
                  </Typography>
                </Typography>
              </Row>
            )}

            {isLoading && (
              <Row
                align="center"
                justify="center"
                style={{ width: '100%', flex: 1, minHeight: 300 }}
              >
                <Spinner />
              </Row>
            )}

            {!isLoading && (
              <>
                <CardList
                  selectedPayment={selectedPayment}
                  setSelectedPayment={setSelectedPayment}
                />
                <BankList
                  selectedPayment={selectedPayment}
                  setSelectedPayment={setSelectedPayment}
                />
              </>
            )}
          </Column>
        </Screen.Body>
      </Screen>
      <SuspendedPostingsPopup />
    </>
  );
}
